import { useState, useMemo, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Package,
  Truck,
  Clock,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Calendar,
  User,
  FileText,
  MapPin,
  Search,
  Filter,
  ArrowRight,
  Download,
  Printer,
  RefreshCw,
  ClipboardList,
  Building,
  DollarSign,
  Hash,
  ExternalLink,
  ChevronRight,
  Activity
} from "lucide-react";
import { toast } from "sonner";
import { useNavigation } from "@/components/layout/app-layout";
import { StaticMetricCardAlt } from "@/components/StaticMetricCardAlt";
import { HelpButton } from "@/components/help-button";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { useContextualHelp } from "@/hooks/use-contextual-help";

// Types
interface PendingReceipt {
  id: string;
  poNumber: string;
  supplierCode: string;
  supplierName: string;
  orderDate: Date;
  expectedDeliveryDate: Date;
  status: "on-time" | "overdue" | "urgent";
  linesOutstanding: number;
  totalLines: number;
  totalValue: number;
  currency: string;
  daysUntilDue: number;
  priority: "HIGH" | "MEDIUM" | "LOW";
}

interface PendingDispatch {
  id: string;
  soNumber: string;
  customerCode: string;
  customerName: string;
  orderDate: Date;
  expectedDispatchDate: Date;
  status: "on-time" | "overdue" | "urgent";
  linesOutstanding: number;
  totalLines: number;
  totalValue: number;
  currency: string;
  daysUntilDue: number;
  priority: "HIGH" | "MEDIUM" | "LOW";
  readyToPick: boolean;
}

interface RecentActivity {
  id: string;
  type: "receipt" | "dispatch";
  reference: string;
  description: string;
  user: string;
  timestamp: Date;
  quantity: number;
  value: number;
}

// Mock data
const MOCK_PENDING_RECEIPTS: PendingReceipt[] = [
  {
    id: "1",
    poNumber: "PO-2024-001",
    supplierCode: "SUP-001",
    supplierName: "Supplier A Ltd",
    orderDate: new Date("2024-01-15"),
    expectedDeliveryDate: new Date("2024-02-15"),
    status: "on-time",
    linesOutstanding: 2,
    totalLines: 3,
    totalValue: 9000.00,
    currency: "GBP",
    daysUntilDue: 5,
    priority: "MEDIUM"
  },
  {
    id: "2",
    poNumber: "PO-2024-002",
    supplierCode: "SUP-002",
    supplierName: "Supplier B Corp",
    orderDate: new Date("2024-01-20"),
    expectedDeliveryDate: new Date("2024-01-25"),
    status: "overdue",
    linesOutstanding: 1,
    totalLines: 1,
    totalValue: 8500.00,
    currency: "GBP",
    daysUntilDue: -5,
    priority: "HIGH"
  },
  {
    id: "3",
    poNumber: "PO-2024-003",
    supplierCode: "SUP-003",
    supplierName: "Supplier C Inc",
    orderDate: new Date("2024-01-25"),
    expectedDeliveryDate: new Date("2024-02-10"),
    status: "on-time",
    linesOutstanding: 3,
    totalLines: 3,
    totalValue: 12500.00,
    currency: "GBP",
    daysUntilDue: 10,
    priority: "LOW"
  },
  {
    id: "4",
    poNumber: "PO-2024-004",
    supplierCode: "SUP-001",
    supplierName: "Supplier A Ltd",
    orderDate: new Date("2024-02-01"),
    expectedDeliveryDate: new Date("2024-02-12"),
    status: "urgent",
    linesOutstanding: 2,
    totalLines: 2,
    totalValue: 6750.00,
    currency: "GBP",
    daysUntilDue: 2,
    priority: "HIGH"
  },
  {
    id: "5",
    poNumber: "PO-2024-005",
    supplierCode: "SUP-004",
    supplierName: "Supplier D Ltd",
    orderDate: new Date("2024-02-03"),
    expectedDeliveryDate: new Date("2024-02-20"),
    status: "on-time",
    linesOutstanding: 1,
    totalLines: 2,
    totalValue: 4200.00,
    currency: "GBP",
    daysUntilDue: 15,
    priority: "LOW"
  }
];

const MOCK_PENDING_DISPATCHES: PendingDispatch[] = [
  {
    id: "1",
    soNumber: "SO-2024-001",
    customerCode: "CUST-001",
    customerName: "ABC Retail Ltd",
    orderDate: new Date("2024-01-15"),
    expectedDispatchDate: new Date("2024-02-15"),
    status: "on-time",
    linesOutstanding: 1,
    totalLines: 3,
    totalValue: 8100.00,
    currency: "GBP",
    daysUntilDue: 5,
    priority: "MEDIUM",
    readyToPick: true
  },
  {
    id: "2",
    soNumber: "SO-2024-002",
    customerCode: "CUST-002",
    customerName: "XYZ Manufacturing",
    orderDate: new Date("2024-01-20"),
    expectedDispatchDate: new Date("2024-01-28"),
    status: "overdue",
    linesOutstanding: 2,
    totalLines: 2,
    totalValue: 15600.00,
    currency: "GBP",
    daysUntilDue: -2,
    priority: "HIGH",
    readyToPick: true
  },
  {
    id: "3",
    soNumber: "SO-2024-003",
    customerCode: "CUST-003",
    customerName: "Tech Solutions Inc",
    orderDate: new Date("2024-01-25"),
    expectedDispatchDate: new Date("2024-02-18"),
    status: "on-time",
    linesOutstanding: 3,
    totalLines: 3,
    totalValue: 22500.00,
    currency: "GBP",
    daysUntilDue: 8,
    priority: "LOW",
    readyToPick: false
  },
  {
    id: "4",
    soNumber: "SO-2024-004",
    customerCode: "CUST-001",
    customerName: "ABC Retail Ltd",
    orderDate: new Date("2024-02-01"),
    expectedDispatchDate: new Date("2024-02-12"),
    status: "urgent",
    linesOutstanding: 2,
    totalLines: 2,
    totalValue: 9800.00,
    currency: "GBP",
    daysUntilDue: 2,
    priority: "HIGH",
    readyToPick: true
  },
  {
    id: "5",
    soNumber: "SO-2024-005",
    customerCode: "CUST-004",
    customerName: "Global Distributors",
    orderDate: new Date("2024-02-03"),
    expectedDispatchDate: new Date("2024-02-25"),
    status: "on-time",
    linesOutstanding: 1,
    totalLines: 1,
    totalValue: 5400.00,
    currency: "GBP",
    daysUntilDue: 15,
    priority: "LOW",
    readyToPick: true
  }
];

const MOCK_RECENT_ACTIVITY: RecentActivity[] = [
  {
    id: "1",
    type: "receipt",
    reference: "PO-2024-001",
    description: "Received 100 units of Widget A from Supplier A Ltd",
    user: "John Smith",
    timestamp: new Date("2024-02-10T10:30:00"),
    quantity: 100,
    value: 4500.00
  },
  {
    id: "2",
    type: "dispatch",
    reference: "SO-2024-001",
    description: "Dispatched 100 units of Widget A to ABC Retail Ltd",
    user: "Sarah Johnson",
    timestamp: new Date("2024-02-10T09:15:00"),
    quantity: 100,
    value: 8100.00
  },
  {
    id: "3",
    type: "receipt",
    reference: "PO-2024-003",
    description: "Received 50 units of Component D from Supplier C Inc",
    user: "John Smith",
    timestamp: new Date("2024-02-09T14:45:00"),
    quantity: 50,
    value: 3750.00
  },
  {
    id: "4",
    type: "dispatch",
    reference: "SO-2024-002",
    description: "Dispatched 75 units of Widget B to XYZ Manufacturing",
    user: "Sarah Johnson",
    timestamp: new Date("2024-02-09T11:20:00"),
    quantity: 75,
    value: 7800.00
  },
  {
    id: "5",
    type: "receipt",
    reference: "PO-2024-002",
    description: "Received 25 units of Raw Material Y from Supplier B Corp",
    user: "Mike Wilson",
    timestamp: new Date("2024-02-08T16:00:00"),
    quantity: 25,
    value: 2125.00
  }
];

// Helper functions
const formatCurrency = (value: number, currency: string = "GBP") => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currency,
  }).format(value);
};

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatDateTime = (date: Date) => {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case "overdue":
      return <Badge variant="destructive">Overdue</Badge>;
    case "urgent":
      return <Badge variant="warning">Urgent</Badge>;
    case "on-time":
      return <Badge variant="success">On Time</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
};

const getPriorityBadge = (priority: string) => {
  switch (priority) {
    case "HIGH":
      return <Badge variant="destructive">High</Badge>;
    case "MEDIUM":
      return <Badge variant="warning">Medium</Badge>;
    case "LOW":
      return <Badge variant="secondary">Low</Badge>;
    default:
      return <Badge variant="secondary">{priority}</Badge>;
  }
};

export function GoodsInOutPage() {
  const { navigateTo } = useNavigation();
  const { isHelpOpen, helpContent, toggleHelp, setIsHelpOpen } = useContextualHelp("goods-in-out");
  const [activeTab, setActiveTab] = useState("receipts");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");

  // Calculate KPIs
  const kpis = useMemo(() => {
    const totalPendingReceipts = MOCK_PENDING_RECEIPTS.length;
    const overdueReceipts = MOCK_PENDING_RECEIPTS.filter(r => r.status === "overdue").length;
    const totalReceiptValue = MOCK_PENDING_RECEIPTS.reduce((sum, r) => sum + r.totalValue, 0);

    const totalPendingDispatches = MOCK_PENDING_DISPATCHES.length;
    const overdueDispatches = MOCK_PENDING_DISPATCHES.filter(d => d.status === "overdue").length;
    const totalDispatchValue = MOCK_PENDING_DISPATCHES.reduce((sum, d) => sum + d.totalValue, 0);
    const readyToPickCount = MOCK_PENDING_DISPATCHES.filter(d => d.readyToPick).length;

    return {
      totalPendingReceipts,
      overdueReceipts,
      totalReceiptValue,
      totalPendingDispatches,
      overdueDispatches,
      totalDispatchValue,
      readyToPickCount
    };
  }, []);

  // Filter receipts
  const filteredReceipts = useMemo(() => {
    return MOCK_PENDING_RECEIPTS.filter(receipt => {
      const matchesSearch = 
        receipt.poNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        receipt.supplierName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        receipt.supplierCode.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === "all" || receipt.status === statusFilter;
      const matchesPriority = priorityFilter === "all" || receipt.priority === priorityFilter;

      return matchesSearch && matchesStatus && matchesPriority;
    });
  }, [searchQuery, statusFilter, priorityFilter]);

  // Filter dispatches
  const filteredDispatches = useMemo(() => {
    return MOCK_PENDING_DISPATCHES.filter(dispatch => {
      const matchesSearch = 
        dispatch.soNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        dispatch.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        dispatch.customerCode.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === "all" || dispatch.status === statusFilter;
      const matchesPriority = priorityFilter === "all" || dispatch.priority === priorityFilter;

      return matchesSearch && matchesStatus && matchesPriority;
    });
  }, [searchQuery, statusFilter, priorityFilter]);

  const handleReceiveGoods = (poNumber: string) => {
    toast.success(`Opening receive goods for ${poNumber}`);
    navigateTo("receive-goods");
  };

  const handleRefresh = () => {
    toast.success("Data refreshed");
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Goods In / Out</h1>
          <p className="text-muted-foreground mt-1">
            Manage daily warehouse operations for receiving and dispatching goods
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <HelpButton onClick={toggleHelp} />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StaticMetricCardAlt
          title="Pending Receipts"
          value={kpis.totalPendingReceipts.toString()}
          change={`${kpis.overdueReceipts} overdue`}
          changeLabel="purchase orders"
          icon={Package}
          variant={kpis.overdueReceipts > 0 ? "error" : "info"}
        />
        <StaticMetricCardAlt
          title="Receipt Value"
          value={formatCurrency(kpis.totalReceiptValue)}
          change={`${kpis.totalPendingReceipts} orders`}
          changeLabel="awaiting goods"
          icon={DollarSign}
          variant="info"
        />
        <StaticMetricCardAlt
          title="Pending Dispatches"
          value={kpis.totalPendingDispatches.toString()}
          change={`${kpis.overdueDispatches} overdue`}
          changeLabel="sales orders"
          icon={Truck}
          variant={kpis.overdueDispatches > 0 ? "error" : "success"}
        />
        <StaticMetricCardAlt
          title="Total Dispatch Value"
          value={formatCurrency(kpis.totalDispatchValue)}
          change={`${kpis.totalPendingDispatches} orders`}
          changeLabel="awaiting dispatch"
          icon={DollarSign}
          variant="info"
        />
      </div>

      {/* Quick Actions */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 border-blue-200 dark:border-blue-800">
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button 
              variant="outline" 
              className="h-auto gap-2 px-5 py-3"
              onClick={() => navigateTo("receive-goods")}
            >
              <Package className="h-5 w-5" />
              <span className="text-sm font-medium whitespace-nowrap">Receive Goods</span>
            </Button>

            <Button 
              variant="outline" 
              className="h-auto gap-2 px-5 py-3"
              onClick={() => navigateTo("purchase-orders")}
            >
              <ClipboardList className="h-5 w-5" />
              <span className="text-sm font-medium whitespace-nowrap">Purchase Orders</span>
            </Button>

            <Button 
              variant="outline" 
              className="h-auto gap-2 px-5 py-3"
              onClick={() => navigateTo("sales-orders")}
            >
              <FileText className="h-5 w-5" />
              <span className="text-sm font-medium whitespace-nowrap">Sales Orders</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Main Content - Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="receipts" className="gap-2">
            <Package className="h-4 w-4" />
            Pending Receipts
            {kpis.overdueReceipts > 0 && (
              <Badge variant="destructive" className="ml-2">
                {kpis.overdueReceipts}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="dispatches" className="gap-2">
            <Truck className="h-4 w-4" />
            Pending Dispatches
            {kpis.overdueDispatches > 0 && (
              <Badge variant="destructive" className="ml-2">
                {kpis.overdueDispatches}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="activity" className="gap-2">
            <Activity className="h-4 w-4" />
            Recent Activity
          </TabsTrigger>
        </TabsList>

        {/* Filters */}
        {(activeTab === "receipts" || activeTab === "dispatches") && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={`Search ${activeTab === "receipts" ? "purchase orders" : "sales orders"}...`}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="on-time">On Time</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priority</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="MEDIUM">Medium</SelectItem>
                    <SelectItem value="LOW">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pending Receipts Tab */}
        <TabsContent value="receipts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Purchase Orders Awaiting Receipt</CardTitle>
              <CardDescription>
                {filteredReceipts.length} purchase order{filteredReceipts.length !== 1 ? "s" : ""} requiring goods receipt
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredReceipts.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No pending receipts found</p>
                </div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>PO Number</TableHead>
                        <TableHead>Supplier</TableHead>
                        <TableHead>Expected Delivery</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead className="text-right">Lines</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredReceipts.map((receipt) => (
                        <TableRow key={receipt.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-muted-foreground" />
                              {receipt.poNumber}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{receipt.supplierName}</p>
                              <p className="text-xs text-muted-foreground">{receipt.supplierCode}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p>{formatDate(receipt.expectedDeliveryDate)}</p>
                                <p className="text-xs text-muted-foreground">
                                  {receipt.daysUntilDue > 0 
                                    ? `${receipt.daysUntilDue} days` 
                                    : `${Math.abs(receipt.daysUntilDue)} days overdue`}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(receipt.status)}</TableCell>
                          <TableCell>{getPriorityBadge(receipt.priority)}</TableCell>
                          <TableCell className="text-right">
                            <div>
                              <p className="font-medium">{receipt.linesOutstanding}</p>
                              <p className="text-xs text-muted-foreground">of {receipt.totalLines}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(receipt.totalValue, receipt.currency)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleReceiveGoods(receipt.poNumber)}
                              >
                                <Package className="h-4 w-4 mr-2" />
                                Receive
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigateTo("purchase-orders", { transactionId: receipt.poNumber })}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Dispatches Tab */}
        <TabsContent value="dispatches" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Sales Orders Awaiting Dispatch</CardTitle>
              <CardDescription>
                {filteredDispatches.length} sales order{filteredDispatches.length !== 1 ? "s" : ""} requiring dispatch
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredDispatches.length === 0 ? (
                <div className="text-center py-12">
                  <Truck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No pending dispatches found</p>
                </div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>SO Number</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Expected Dispatch</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead className="text-right">Lines</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredDispatches.map((dispatch) => (
                        <TableRow key={dispatch.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-muted-foreground" />
                              {dispatch.soNumber}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{dispatch.customerName}</p>
                              <p className="text-xs text-muted-foreground">{dispatch.customerCode}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p>{formatDate(dispatch.expectedDispatchDate)}</p>
                                <p className="text-xs text-muted-foreground">
                                  {dispatch.daysUntilDue > 0 
                                    ? `${dispatch.daysUntilDue} days` 
                                    : `${Math.abs(dispatch.daysUntilDue)} days overdue`}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(dispatch.status)}
                          </TableCell>
                          <TableCell>{getPriorityBadge(dispatch.priority)}</TableCell>
                          <TableCell className="text-right">
                            <div>
                              <p className="font-medium">{dispatch.linesOutstanding}</p>
                              <p className="text-xs text-muted-foreground">of {dispatch.totalLines}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(dispatch.totalValue, dispatch.currency)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigateTo("sales-orders", { transactionId: dispatch.soNumber })}
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              View Order
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recent Activity Tab */}
        <TabsContent value="activity" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Goods In/Out Activity</CardTitle>
              <CardDescription>
                Latest warehouse transactions for receiving and dispatching goods
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {MOCK_RECENT_ACTIVITY.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-start gap-4 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                  >
                    <div className={`p-2 rounded-lg ${
                      activity.type === "receipt" 
                        ? "bg-blue-100 text-blue-600" 
                        : "bg-green-100 text-green-600"
                    }`}>
                      {activity.type === "receipt" ? (
                        <Package className="h-5 w-5" />
                      ) : (
                        <Truck className="h-5 w-5" />
                      )}
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{activity.description}</p>
                          <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <FileText className="h-3 w-3" />
                              {activity.reference}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {activity.user}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDateTime(activity.timestamp)}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">{formatCurrency(activity.value)}</p>
                          <p className="text-sm text-muted-foreground">{activity.quantity} units</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Help Drawer */}
      <ContextualHelpDrawer
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title={helpContent?.title || "Goods In / Out"}
        content={helpContent?.content || ""}
      />
    </div>
  );
}
